import queryString from 'query-string';
import { ENDPOINTS } from 'config/endpoints';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { StrategyWithAnalytics } from 'crono-fe-common/types/strategy';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';

const getStrategy = (strategyId?: number | null) => {
  const request: Request = {
    url: `${ENDPOINTS.strategy.main}?${queryString.stringify({ strategyId })}`,
    config: {
      method: 'get',
    },
  };
  return client(request) as Promise<Response<StrategyWithAnalytics>>;
};

export default function useGetStrategy(
  strategyId?: number | null,
  enabled = true,
) {
  const requestConfig = {
    queryKey: [ENDPOINTS.strategy.main, strategyId],
    queryFn: () => getStrategy(strategyId),
    enabled: strategyId !== -1 && !!strategyId && enabled,
  };

  const { data, ...rest } = useQuery<
    Response<StrategyWithAnalytics>,
    CronoError
  >(requestConfig);

  return { data, ...rest };
}

// Shares useGetStrategy's cache, so a sequence fetched by either is fetched once.
export const useGetStrategies = (strategyIds: number[]) =>
  useQueries({
    queries: strategyIds.map((strategyId) => ({
      queryKey: [ENDPOINTS.strategy.main, strategyId],
      queryFn: () => getStrategy(strategyId),
    })),
  });

export const useInvalidateGetStrategy = () => {
  const queryClient = useQueryClient();
  return ({
    strategyId,
  }: {
    strategyId?: number;
  } = {}) =>
    queryClient.invalidateQueries([ENDPOINTS.strategy.main, strategyId]);
};

export const usePatchStrategyQueryData = () => {
  const queryClient = useQueryClient();
  return (
    strategyId: number,
    strategyUpdates: Partial<StrategyWithAnalytics>,
  ) =>
    queryClient.setQueryData<Response<StrategyWithAnalytics> | undefined>(
      [ENDPOINTS.strategy.main, strategyId],
      (oldData) => {
        if (!oldData) return;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              ...strategyUpdates,
            },
          },
        };
      },
    );
};

/**
 * Replaces one step in the cached strategy with a new object built from the
 * latest cached copy. The cache must never be mutated in place: structural
 * sharing would see no difference and skip notifying observers, leaving
 * anything derived from the steps (e.g. the duration in the header) stale.
 */
export const useUpdateStrategyStepQueryData = () => {
  const queryClient = useQueryClient();
  return (
    strategyId: number,
    stepIndex: number,
    updateStep: (step: StrategyStep) => StrategyStep,
  ) =>
    queryClient.setQueryData<Response<StrategyWithAnalytics> | undefined>(
      [ENDPOINTS.strategy.main, strategyId],
      (oldData) => {
        if (!oldData) return;
        const steps = oldData.data.data.steps;
        if (!steps?.[stepIndex]) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            data: {
              ...oldData.data.data,
              steps: steps.map((step, index) =>
                index === stepIndex ? updateStep(step) : step,
              ),
            },
          },
        };
      },
    );
};
